import React from 'react';
import { Calendar, Clock, IndianRupee, User, FileText, CreditCard, Edit, Trash2, ExternalLink } from 'lucide-react';

const OfflineJobCard = ({ job, onEdit, onDelete, showActions = true }) => {
  const serviceTypeLabels = {
    boarding: 'Boarding',
    walking: 'Walking',
    grooming: 'Grooming',
    sitting: 'Pet Sitting',
    training: 'Training',
    other: 'Other'
  };

  const paymentMethodLabels = {
    cash: 'Cash',
    upi: 'UPI',
    card: 'Card',
    bank_transfer: 'Bank Transfer',
    other: 'Other'
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const totalAmount = job.amount + (job.taxAmount || 0);

  return (
    <div className="bg-white rounded-xl p-4 border-2 border-blue-100 hover:border-blue-200 hover:shadow-md transition-all">
      {/* Header with External badge */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
            <ExternalLink className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{serviceTypeLabels[job.serviceType]}</h3>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                External
              </span>
            </div>
            <p className="text-sm text-gray-600">{job.petName}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-gray-900">₹{totalAmount.toLocaleString()}</p>
          {job.taxAmount > 0 && (
            <p className="text-xs text-gray-500">Inc. tax ₹{job.taxAmount}</p>
          )}
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(job.jobDate)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4" />
          <span>{job.duration} {job.durationUnit}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <User className="w-4 h-4" />
          <span className="truncate">{job.clientName}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <CreditCard className="w-4 h-4" />
          <span>{paymentMethodLabels[job.paymentMethod]}</span>
        </div>
      </div>

      {/* Notes */}
      {job.notes && (
        <div className="mb-3 p-2 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="flex-1">{job.notes}</p>
          </div>
        </div>
      )}

      {/* Receipt Photos */}
      {job.receiptPhotos && job.receiptPhotos.length > 0 && (
        <div className="mb-3 flex gap-2">
          {job.receiptPhotos.slice(0, 3).map((photo, index) => (
            <img
              key={index}
              src={photo}
              alt={`Receipt ${index + 1}`}
              className="w-16 h-16 rounded-lg object-cover border border-gray-200"
            />
          ))}
          {job.receiptPhotos.length > 3 && (
            <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-sm text-gray-600 font-medium border border-gray-200">
              +{job.receiptPhotos.length - 3}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {showActions && (
        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
          <button
            onClick={() => onEdit(job)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => onDelete(job.id)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default OfflineJobCard;

